import * as React from "react"
import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { useMutation, useQuery, useQueryClient } from "react-query"

const BASE = process.env.REACT_APP_API_BASE_URL || ""

const handle = async (r) => {
  const body = await r.json().catch(() => null)
  if (!r.ok) throw new Error(body?.message || body?.error || `status ${r.status}`)
  return body
}

const getScenes = () =>
  fetch(`${BASE}/api/admin/scenes`, { credentials: "include" }).then(handle)

const createScene = (fields) =>
  fetch(`${BASE}/api/admin/scenes`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  }).then(handle)

const deleteScene = (id) =>
  fetch(`${BASE}/api/admin/scenes/${id}`, {
    method: "DELETE",
    credentials: "include",
  }).then(handle)

const uploadSplat = (file) => {
  const form = new FormData()
  form.append("file", file)
  return fetch(`${BASE}/api/admin/splats`, {
    method: "POST",
    credentials: "include",
    body: form,
  }).then(handle)
}

export default function ScenesPanel() {
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    slug: "", name: "", board_id: "", kind: "board", splat_file: "",
  })

  const { data } = useQuery("adminScenes", getScenes, { retry: false })
  const invalidate = () => queryClient.invalidateQueries("adminScenes")
  const onErr = (e) => setError(e.message)
  const create = useMutation(createScene, {
    onError: onErr,
    onSuccess: () => {
      setError(null)
      setForm({ slug: "", name: "", board_id: "", kind: "board", splat_file: "" })
    },
    onSettled: invalidate,
  })
  const remove = useMutation(deleteScene, { onError: onErr, onSettled: invalidate })
  const upload = useMutation(uploadSplat, {
    onError: onErr,
    onSuccess: () => setError(null),
    onSettled: invalidate,
  })

  const scenes = data?.scenes || []
  const splats = data?.splats || []
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  // registering a scene needs slug/name/board_id; auto-suggest from splat pick
  const pickSplat = (e) => {
    const file = e.target.value
    const base = file.replace(/\.\w+$/, "")
    setForm({
      ...form,
      splat_file: file,
      slug: form.slug || base,
      name: form.name || base.replace(/-/g, " "),
      board_id: form.board_id || base,
    })
  }

  return (
    <Box sx={{ maxWidth: 860, mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Register a scene
            </Typography>
            <Button component="label" size="small" startIcon={<UploadFileIcon />}
              disabled={upload.isLoading}>
              {upload.isLoading ? "Uploading…" : "Upload splat"}
              <input
                type="file"
                hidden
                accept=".splat,.spz,.ksplat,.ply"
                onChange={(e) => {
                  if (e.target.files[0]) upload.mutate(e.target.files[0])
                  e.target.value = ""
                }}
              />
            </Button>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select label="Splat file" size="small" sx={{ minWidth: 240 }}
              value={form.splat_file} onChange={pickSplat}>
              {splats.map((s) => (
                <MenuItem key={s.file} value={s.file}>
                  {s.file} ({s.size_mb} MB){s.in_use ? " — in use" : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Slug" size="small" value={form.slug} onChange={set("slug")} />
            <TextField label="Name" size="small" value={form.name} onChange={set("name")} />
            <TextField label="Board id (pipeline model)" size="small"
              value={form.board_id} onChange={set("board_id")}
              helperText="must match boards.py on the pod volume" />
            <TextField select label="Kind" size="small" sx={{ minWidth: 110 }}
              value={form.kind} onChange={set("kind")}>
              <MenuItem value="board">board</MenuItem>
              <MenuItem value="wall">wall</MenuItem>
              <MenuItem value="boulder">boulder</MenuItem>
            </TextField>
            <Button variant="contained"
              disabled={create.isLoading || !form.slug || !form.name || !form.board_id}
              onClick={() => create.mutate(form)}>
              Register
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Scenes</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Slug</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Board id</TableCell>
                <TableCell>Splat</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {scenes.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.slug}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell><Chip size="small" label={s.kind} /></TableCell>
                  <TableCell>{s.board_id}</TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: "hidden",
                                   textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(s.splat_url || "").split("/").pop()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete (blocked if clips use it)">
                      <IconButton size="small" onClick={() => {
                        if (window.confirm(`Delete scene ${s.slug}?`)) remove.mutate(s.id)
                      }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  )
}
